"use client";
import { usePublicT } from "../../public-locale";


import Link from "./MarketingLink";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { checkoutErrorMessage, checkoutRecoveryLinks, requestCheckout, type CheckoutSlug } from "../../../lib/billing-checkout";
import { useMarketingAppLinks } from "../contexts/MarketingAppLinkContext";

type CheckoutContextValue = { active: CheckoutSlug | null; start: (slug: CheckoutSlug) => Promise<boolean> };
const CheckoutContext = createContext<CheckoutContextValue | null>(null);
export function CheckoutProvider({ children }: { children: ReactNode }) {
  const t = usePublicT();
  const [active, setActive] = useState<CheckoutSlug | null>(null);
  const locked = useRef(false);
  const requests = useRef(new Map<CheckoutSlug, string>());
  useEffect(() => {
    function restore(event: PageTransitionEvent) {
      if (!event.persisted) return;
      locked.current = false;
      requests.current.clear();
      setActive(null);
    }
    window.addEventListener("pageshow", restore);
    return () => window.removeEventListener("pageshow", restore);
  }, []);
  async function start(slug: CheckoutSlug) {
    if (locked.current) return false;
    locked.current = true;
    setActive(slug);
    try {
      const key = requests.current.get(slug) || crypto.randomUUID();
      requests.current.set(slug, key);
      const url = await requestCheckout(slug, key);
      window.location.assign(url);
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === "CHECKOUT_EXPIRED") requests.current.delete(slug);
      locked.current = false;
      setActive(null);
      throw error;
    }
  }
  return <CheckoutContext.Provider value={{ active, start }}>{t(children)}</CheckoutContext.Provider>;
}

export default function CheckoutButton({ plan, annual = false, highlight = false, preview = false }: { plan: "Start" | "Growth" | "Pro" | "Enterprise"; annual?: boolean; highlight?: boolean; preview?: boolean }) {
  const t = usePublicT();
  const context = useContext(CheckoutContext);
  const [error, setError] = useState("");
  const { signInHref } = useMarketingAppLinks();
  const className = `marketing-checkout-button${highlight ? " marketing-checkout-highlight" : ""}${preview ? " marketing-checkout-preview" : ""}`;
  if (plan === "Enterprise") return <Link href="/enterprise#enterprise-contact" className={className}>{t("Contact sales")}</Link>;
  const slug = `${plan.toLowerCase()}-${annual ? "annual" : "monthly"}` as CheckoutSlug;
  async function begin() {
    setError("");
    try {
      if (!context) throw new Error("CHECKOUT_UNAVAILABLE");
      await context.start(slug);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "CHECKOUT_UNAVAILABLE"); }
  }
  const signIn = error === "CHECKOUT_AUTH_REQUIRED";
  const workspace = error === "CHECKOUT_WORKSPACE_REQUIRED" || error === "CHECKOUT_BILLING_REQUIRED";
  const recovery = checkoutRecoveryLinks(signInHref);
  return <div className={highlight ? "marketing-checkout-feedback highlight" : "marketing-checkout-feedback"}>
    <button type="button" onClick={begin} disabled={Boolean(context?.active)} aria-busy={context?.active === slug} className={className}>
      {t(context?.active === slug ? "Opening checkout…" : `Choose ${plan}`)}
    </button>
    {t(error && <div role="alert" className="marketing-checkout-error"><p>{t(checkoutErrorMessage(error))}</p>
      {t(signIn ? <a href={recovery.signIn}>{t("Sign in")}</a> : workspace ? <a href={recovery.workspace}>{t("Open workspace")}</a> : <Link href="/contact">{t("Contact us")}</Link>)}
    </div>)}
  </div>;
}
